import React, { useState, useEffect, useRef } from 'react';
import { SPM1_DATA, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Eye, EyeOff, Play, Pause, RotateCcw } from 'lucide-react';

interface SPM1HistoryProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM1History: React.FC<SPM1HistoryProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm1'];
  const audioEngine = AudioEngine.getInstance();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [zoomModern, setZoomModern] = useState<boolean>(true);
  const [muteNatural, setMuteNatural] = useState<boolean>(false);
  const [muteHuman, setMuteHuman] = useState<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Filter dataset based on zoom
  const dataset = zoomModern
    ? SPM1_DATA.filter((d) => d.year >= 1850)
    : SPM1_DATA;

  // Animation Loop for timeline playback
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % dataset.length);
      }, 600);
    } else {
      audioEngine.stopNatWarmth();
      audioEngine.stopAnthropoPulse();
    }
    return () => {
      clearInterval(timer);
    };
  }, [isPlaying, dataset.length]);

  // Audio Update per index change
  useEffect(() => {
    if (!audioEngine.getIsUnlocked()) return;

    const currentPoint = dataset[currentIndex];
    if (!currentPoint) return;

    // Natural Forcing Sound
    if (isPlaying && !muteNatural) {
      const natFreq = audioEngine.tempToFreq(currentPoint.naturalForcing);
      audioEngine.startNatWarmth(natFreq, 0.25);
    } else {
      audioEngine.stopNatWarmth();
    }

    // Human Forcing Sound
    if (isPlaying && !muteHuman) {
      const humFreq = audioEngine.tempToFreq(currentPoint.humanForcing);
      // FM index increases with human forcing magnitude
      const fmIdx = 2 + Math.max(0, currentPoint.humanForcing) * 6;
      audioEngine.startAnthropoPulse(humFreq, fmIdx, 0.35);
    } else {
      audioEngine.stopAnthropoPulse();
    }
  }, [currentIndex, isPlaying, muteNatural, muteHuman, dataset]);

  // Canvas Drawing Effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = (canvas.width = canvas.parentElement?.clientWidth || 800);
    const height = (canvas.height = 320);

    ctx.clearRect(0, 0, width, height);

    // Padding & Axes Setup
    const padX = 50;
    const padY = 40;
    const chartW = width - padX * 2;
    const chartH = height - padY * 2;

    const minYear = dataset[0].year;
    const maxYear = dataset[dataset.length - 1].year;
    const minTemp = -0.5;
    const maxTemp = 1.6;

    const getX = (yr: number) => padX + ((yr - minYear) / (maxYear - minYear)) * chartW;
    const getY = (t: number) => padY + chartH - ((t - minTemp) / (maxTemp - minTemp)) * chartH;

    // Grid lines & Zero Baseline
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.15)';
    ctx.lineWidth = 1;
    for (let t = -0.4; t <= 1.4; t += 0.4) {
      const y = getY(t);
      ctx.beginPath();
      ctx.moveTo(padX, y);
      ctx.lineTo(padX + chartW, y);
      ctx.stroke();

      ctx.fillStyle = '#64748b';
      ctx.font = '10px monospace';
      ctx.fillText(`${t > 0 ? '+' : ''}${t.toFixed(1)}°C`, 10, y + 3);
    }

    // 0°C Reference line
    const zeroY = getY(0);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.4)';
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(padX, zeroY);
    ctx.lineTo(padX + chartW, zeroY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw Natural Forcing Curve (Green/Amber)
    if (!muteNatural) {
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      dataset.forEach((pt, i) => {
        const x = getX(pt.year);
        const y = getY(pt.naturalForcing);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    // Draw Human Forcing Curve (Orange/Red)
    if (!muteHuman) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      dataset.forEach((pt, i) => {
        const x = getX(pt.year);
        const y = getY(pt.humanForcing);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    // Draw Observed Temperature (Cyan Dots & Line)
    ctx.strokeStyle = '#38bdf8';
    ctx.fillStyle = '#0284c7';
    ctx.lineWidth = 2;
    ctx.beginPath();
    let started = false;
    dataset.forEach((pt) => {
      if (pt.observedTemp !== null) {
        const x = getX(pt.year);
        const y = getY(pt.observedTemp);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
    });
    ctx.stroke();

    // Active Scrubber Line & Glow
    const activePt = dataset[currentIndex];
    if (activePt) {
      const scrubX = getX(activePt.year);
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 2;
      ctx.shadowColor = '#06b6d4';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(scrubX, padY);
      ctx.lineTo(scrubX, padY + chartH);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Active Node Dot
      const humY = getY(activePt.humanForcing);
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(scrubX, humY, 5, 0, Math.PI * 2);
      ctx.fill();

      // Year Text Anchor
      ctx.fillStyle = '#f8fafc';
      ctx.font = 'bold 12px monospace';
      ctx.fillText(`Year ${activePt.year}`, scrubX - 25, padY - 10);
    }
  }, [dataset, currentIndex, muteNatural, muteHuman]);

  const activePt = dataset[currentIndex] || dataset[0];

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        {/* Zoom & Solo Controls */}
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
            <button
              onClick={() => { setZoomModern(false); setCurrentIndex(0); }}
              className={`px-3 py-1 rounded-lg font-mono font-medium transition-colors ${!zoomModern ? 'bg-cyan-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'}`}
            >
              1–2020 AD (Paleo)
            </button>
            <button
              onClick={() => { setZoomModern(true); setCurrentIndex(0); }}
              className={`px-3 py-1 rounded-lg font-mono font-medium transition-colors ${zoomModern ? 'bg-cyan-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'}`}
            >
              1850–2020 AD (Modern)
            </button>
          </div>

          <button
            onClick={() => setCurrentIndex(0)}
            className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
            title="Reset Timeline"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Interactive Canvas */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-4">
        <canvas ref={canvasRef} className="w-full h-80 rounded-xl bg-slate-950/80 cursor-pointer" />

        {/* Timeline Scrubber Slider */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs font-mono text-slate-400">
            <span>{dataset[0].year} AD</span>
            <span className="text-cyan-300 font-bold">Selected Year: {activePt.year}</span>
            <span>{dataset[dataset.length - 1].year} AD</span>
          </div>
          <input
            type="range"
            min="0"
            max={dataset.length - 1}
            value={currentIndex}
            onChange={(e) => setCurrentIndex(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg bg-slate-800 accent-cyan-400 cursor-pointer"
          />
        </div>

        {/* Polyphonic Duet Solo/Mute Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          {/* Natural Forcing Control */}
          <div className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between ${!muteNatural ? 'bg-emerald-950/30 border-emerald-500/40 text-emerald-200' : 'bg-slate-900/50 border-slate-800 text-slate-500'}`}>
            <div className="space-y-0.5">
              <span className="text-xs font-bold block flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                Simulated Natural Drivers
              </span>
              <span className="text-xs font-mono">
                {activePt.naturalForcing > 0 ? '+' : ''}{activePt.naturalForcing.toFixed(2)}°C (Nat-Warmth Patch)
              </span>
            </div>
            <button
              onClick={() => setMuteNatural(!muteNatural)}
              className="p-2 rounded-xl bg-slate-900 border border-slate-700 hover:text-white"
            >
              {muteNatural ? <EyeOff className="w-4 h-4 text-red-400" /> : <Eye className="w-4 h-4 text-emerald-400" />}
            </button>
          </div>

          {/* Human Forcing Control */}
          <div className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between ${!muteHuman ? 'bg-red-950/30 border-red-500/40 text-red-200' : 'bg-slate-900/50 border-slate-800 text-slate-500'}`}>
            <div className="space-y-0.5">
              <span className="text-xs font-bold block flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span>
                Simulated Human Drivers
              </span>
              <span className="text-xs font-mono">
                {activePt.humanForcing > 0 ? '+' : ''}{activePt.humanForcing.toFixed(2)}°C (Anthropo-Pulse Patch)
              </span>
            </div>
            <button
              onClick={() => setMuteHuman(!muteHuman)}
              className="p-2 rounded-xl bg-slate-900 border border-slate-700 hover:text-white"
            >
              {muteHuman ? <EyeOff className="w-4 h-4 text-red-400" /> : <Eye className="w-4 h-4 text-red-400" />}
            </button>
          </div>
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
